import { useQuery } from '@tallink/more-react-hooks'
import { format } from 'date-fns'
import PropTypes from 'prop-types'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import {
  CwsA,
  CwsAccordion,
  CwsAccordionItem,
  CwsAccordionItemBody,
  CwsAccordionItemHeader,
  CwsDivider,
  CwsGrid,
  CwsGridCol,
  CwsGridRow,
  CwsIcon,
  CwsP,
  CwsSection,
  CwsSpan
} from '../../cms-web-styleguide-exports'
import { getDigiData } from '../../selectors/selectors'
import Tooltip from '../common/Tooltip'
import UnstyledButton from '../common/UnstyledButton'
import s from './Coupons.module.scss'

export default function CouponAccordion({ groupedCoupons, activeTab }) {
  const DEFAULT_LOCALE = 'fi'
  const LOCALE_TO_COUNTRY = {
    en: 'XZ',
    et: 'EE',
    fi: 'FI',
    lt: 'LV',
    lv: 'LV',
    ru: 'EE',
    sv: 'FI'
  }
  const COUPON_CLASS_TO_ICON = {
    'SERIAL VAN': 'vehicle-van',
    'SERIAL CAR/CAR XL': 'vehicle-car'
  }
  const localeToCountry = locale => LOCALE_TO_COUNTRY[locale] || LOCALE_TO_COUNTRY[DEFAULT_LOCALE]
  const accordionIcon = couponClass => COUPON_CLASS_TO_ICON[couponClass] || 'passengers'
  const localeFromQuery = useQuery('locale')
  const { t } = useTranslation()
  const digiData = useSelector(getDigiData)
  const locale = digiData?.website?.locale || localeFromQuery || DEFAULT_LOCALE
  const country = localeToCountry(locale)

  const copyCouponCodeToClipboard = code => {
    try {
      navigator.clipboard.writeText(code)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to copy coupon code to clipboard: ', error)
    }
  }

  return (
    <CwsAccordion mode="card" size="large" variant="multiple">
      {Object.entries(groupedCoupons ?? {}).map(([couponClass, byDate]) => (
        <CwsAccordionItem className={`${s['override-cws-color']} cws-shadow-drop-m`} id={couponClass} key={couponClass}>
          <CwsAccordionItemHeader
            icon={accordionIcon(couponClass)}
            label={t(couponClass)}
            tagLabel={Object.values(byDate).flat().length}
          />
          <CwsAccordionItemBody style={{ maxWidth: '960px' }}>
            {activeTab === 'used' && (
              <CwsGrid width="fluid">
                <CwsGridRow className="cws-gx-xxl cws-gy-xs">
                  {Object.values(byDate)
                    .flat()
                    .map(coupon => (
                      <CwsGridCol col="auto" key={coupon.id}>
                        <CwsSection>
                          <CwsSpan
                            className="cws-mr-xs"
                            style={{ minWidth: '95px', display: 'inline-block' }}
                            weight="500"
                          >
                            {coupon.code}
                          </CwsSpan>
                        </CwsSection>
                      </CwsGridCol>
                    ))}
                </CwsGridRow>
              </CwsGrid>
            )}

            {(activeTab === 'unused' || activeTab === 'expired') &&
              Object.entries(byDate).map(([validUntil, coupons], index, array) => (
                <CwsSection key={validUntil} variant="div">
                  {activeTab === 'unused' && (
                    <CwsP className="cws-mb-xs" size="p">
                      {t('ticket.expiry.notice')} {format(new Date(validUntil), 'dd.MM.yyyy')}
                    </CwsP>
                  )}
                  {activeTab === 'expired' && (
                    <CwsP className="cws-mb-xs" size="p">
                      {t('ticket.expired')} {format(new Date(validUntil), 'dd.MM.yyyy')}
                    </CwsP>
                  )}
                  <CwsGrid width="fluid">
                    <CwsGridRow className="cws-gx-xxl cws-gy-xs">
                      {coupons.map(coupon => (
                        <CwsGridCol col="auto" key={coupon.id}>
                          <CwsSection>
                            <CwsSpan
                              className="cws-mr-xs"
                              style={{ minWidth: '95px', display: 'inline-block' }}
                              weight="500"
                            >
                              {coupon.code}
                            </CwsSpan>
                            {activeTab === 'unused' && (
                              <>
                                <UnstyledButton
                                  ariaLabel={t('copy.coupon.to.clipboard.accessibility.label')}
                                  onClick={() => copyCouponCodeToClipboard(coupon.code)}
                                >
                                  <CwsIcon className={`cws-mr-xs ${s['copy-icon']}`} icon="copy" size="16px" />
                                </UnstyledButton>
                                <Tooltip content={<CwsSpan>{t('button.book.a.trip')}</CwsSpan>}>
                                  <CwsA
                                    href={`https://travel.tallink.com/book?voucherId=${coupon.code}&country=${country}&locale=${locale}`}
                                    target="_blank"
                                  />
                                </Tooltip>
                              </>
                            )}
                          </CwsSection>
                        </CwsGridCol>
                      ))}
                    </CwsGridRow>
                  </CwsGrid>
                  {index < array.length - 1 && <CwsDivider spacing="s" variant="dashed" />}
                </CwsSection>
              ))}
          </CwsAccordionItemBody>
        </CwsAccordionItem>
      ))}
    </CwsAccordion>
  )
}

CouponAccordion.propTypes = {
  activeTab: PropTypes.string.isRequired,
  groupedCoupons: PropTypes.object.isRequired
}
